import { Injectable } from '@angular/core';
import { ApiService } from '../../api/api.service';
import { ProjectService } from '../project/project.service';
import { BasicAreaCoreService } from './basic-area-core.service';
import { AreaService } from '../area/area.service';
import { BasicArea } from './basic-area.interface';
import { ModuleService } from '../module/module.service';
import { ProjectModule } from '../module/project-module.interface';
import { Observable } from 'rxjs';
import { Area } from '../area/area.interface';
import { ConstantsQualityLevel } from '../../utilities/constants/constants.interface';
import { ConstantsService } from '../../utilities/constants/constants.service';
import { ElementService } from '../element/element.service';
import { ModalService } from '../../services/modal/modal.service';
import { TranslateService } from '../../utilities/translate/translate.service';
import { ToastService } from '../../services/toast/toast.service';
import { ApiLegacyService } from '../../api/legacy/api-legacy.service';

/**
 * Basic area service.
 *
 * The public interface for the BasicAreaCoreService, with shorthands for
 * each operation.
 */
@Injectable({
  providedIn: 'root'
})
export class BasicAreaService extends BasicAreaCoreService {

  /** Constructor. */
  constructor(
    protected apiService: ApiService,
    protected apiLegacyService: ApiLegacyService,
    protected areaService: AreaService,
    private constantsService: ConstantsService,
    private elementService: ElementService,
    private modalService: ModalService,
    private moduleService: ModuleService,
    protected projectService: ProjectService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    super(apiService, apiLegacyService, areaService, projectService);
  }

  /**
   * Delete area template.
   *
   * @param areaTemplate The area template to delete.
   * @param confirm Whether to show confirm modal before deletion.
   * @returns Deletion as observable.
   */
  public delete(areaTemplate: BasicArea, confirm: boolean = false): Observable<boolean> {
    if (confirm) {
      this.modalService.confirm({
        title: this.translateService.get('deleteareatemplate'),
        content: this.translateService.get('areyousuredeletingareatemplate'),
        action: this.translateService.getPlural('delete'),
        actionIcon: 'delete',
        type: 'warn'
      }).afterClosed().subscribe((click: any) => {
        if (click === 'confirm') {
          this.delete(areaTemplate, false).subscribe();
        }
      });
    } else {
      return new Observable(observer => {
        this._delete(areaTemplate).subscribe(() => {
          this.toastService.open(this.translateService.get('deletedAreaTemplates'));
          observer.next(true);
          observer.complete();
        });
      });
    }
  }

  /**
   * Get a list of all the requirements for the current basic area.
   *
   * @param id Basic area id.
   * @returns Filtered list of modules with the requested basic area's requirements.
   */
  public getRequirements(id: BasicArea['_id']): ProjectModule[] {
    const basicArea = this.getBasicArea(id);
    const modules = this.moduleService.get().filter(m => m.isTechnicalType);
    for (const module of modules) {
      for (const submodule of module.submodules) {
        const requirements = [];
        submodule.elements.filter(e => e.active && [basicArea._id].includes(e.constructionId)).forEach(e => {
          const templateValue = e.templateValues && e.templateValues.find(t => t.quality === e.quality);
          requirements.push({
            ...e,
            originalValue: templateValue && templateValue.value || null
          });
        });
        submodule.requirements = requirements;
      }
    }
    return modules;
  }

  /**
   * Get list of area's who use the basic area as their template.
   *
   * @param basicArea The basic area used as template for the areas.
   * @returns List of areas.
   */
  public getConnectedAreas(basicArea: BasicArea): Area[] {
    return this.areaService.getAreasByBasicAreaId(basicArea._id);
  }

  /**
   * Patch the basic area with multiple values at once.
   *
   * @note Only use this when there's no other option available! I.e. when you need to
   *  patch multiple values which are not known in advance. Because the values can be
   *  *anything*, there's no integrity check when patching values with this method.
   *
   * @param basicArea The basic area to patch.
   * @param values A (partial) set of area values to patch.
   * @param config Optional config object.
   * @returns API response.
   */
  public patch(basicArea: BasicArea, values: Partial<BasicArea>, config?: any): Observable<any> {
    return this._patchBasicArea(basicArea, values, config);
  }

  /**
   * Set basic area name.
   *
   * @todo Toast.
   *
   * @param basicArea The basic area to set the name for.
   * @param name The new name.
   */
  public setName(basicArea: BasicArea, name: BasicArea['name']): void {
    this._patchBasicArea(basicArea, {
      name: this._checkNameUnique(name)
    }).subscribe(() => {
      this.toastService.open(this.translateService.get('areatemplateupdated'));
    });
  }

  /**
   * Set quality level for all the basic area's requirements.
   *
   * @todo SPEC-2739: Use new endpoint.
   *
   * @param basicArea The basic area to update the requirements for.
   * @param qualityLevel The new quality level.
   */
  public setQualityLevel(basicArea: BasicArea, qualityLevel: ConstantsQualityLevel['value']): void {
    this.modalService.confirm({
      title: this.translateService.get('areyousurechangeareatemplatequalitylevel'),
      content: this.translateService.get('areyousurechangeareatemplatequalitylevelexplanation')
    }).afterClosed().subscribe((click: any) => {
      if (click === 'confirm') {
        const qualityLevels = this.constantsService.getQualityLevels();
        const qualityLevelCurrent = qualityLevels.find(ql => ql.value === basicArea.qualityLevel);
        const qualityLevelUpdated = qualityLevels.find(ql => ql.value === qualityLevel);
        const name = basicArea.name
          .replace(/\([0-9]*\)/, '').trim()
          .replace(`(${ qualityLevelCurrent.displayName })`, `(${ qualityLevelUpdated.displayName })`);

        this.apiService.updateBasicAreaQualityLevel(basicArea, qualityLevel).subscribe(() => {
          basicArea.name = this._checkNameUnique(name);
          this._patchBasicArea(basicArea, { name: basicArea.name }).subscribe(() => {
            this.projectService.patchBasicArea(basicArea, { qualityLevel: qualityLevel });
            this._setQualityLevelForRequirements(basicArea, qualityLevel);
            this.toastService.open(this.translateService.get('qualityLevelUpdated'));
          });
        });
      }
    });
  }

  /**
   * After updating the basic area quality level, the requirements
   * should be synced with the server (update quality level and
   * value for all).
   *
   * @param basicArea The basic area to update the requirements for.
   * @param qualityLevel The selected quality level.
   */
  private _setQualityLevelForRequirements(basicArea: BasicArea, qualityLevel: ConstantsQualityLevel['value']): void {
    let requirements = [];
    for (const module of this.getRequirements(basicArea._id)) {
      for (const submodule of module.submodules) {
        requirements.push(...submodule.requirements);
      }
    }
    requirements = requirements.filter(e =>
      e.constructionId === basicArea._id
      && e.active
      && e.valuesChanged === false
      && e.status === 1
    );
    for (const element of requirements) {
      this.projectService.patchElement(element, {
        quality: qualityLevel,
        value: element.templateValues.find(tv => tv.quality === qualityLevel).value
      });
    }
  }
}
